import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import { Select } from 'antd';
import AsyncSelect from 'react-select/async';

import { PopupNames } from 'core';
import {
  PaginatedStudentsDocument,
  PaginatedStudentsQuery,
  PaginatedStudentsQueryVariables,
  usePaginatedGroupsQuery,
} from 'core/graphql';
import {
  usePopupData,
  usePopupStateOperations,
  useCareerDirections,
  useCareerMemberMutations,
  IEnrollResult,
} from 'core/hooks';
import { Colors } from 'services/ColorService';

import { PopupContainer } from '../PopupContainer';

const GROUP_SEARCH_DEBOUNCE_MS = 300;

export interface IEnrollCareerMembersPopupData {
  /** Fired after a successful enrolment so the caller can refetch + toast. */
  onEnrolled?: (result: IEnrollResult) => void;
}

interface IStudentOption {
  value: string;
  label: string;
}

/**
 * Spec 0018 — «Добавить участников».
 *
 * Students and a group are additive, not exclusive: the backend unions them,
 * so an operator can enrol a whole cohort plus a couple of stragglers in one
 * pass. Re-running is safe — an already-enrolled student comes back under
 * `skipped`, an archived one under `reactivated`. That breakdown is handed to
 * the caller, which owns the success toast.
 *
 * No `<Modal>` here: `GlobalModal` already portals one around every popup, and
 * a second would stack two fixed backdrops.
 */
export const EnrollCareerMembersPopup = memo(() => {
  const { t } = useTranslation();
  const apollo = useApolloClient();
  const data = usePopupData(PopupNames.enrollCareerMembersPopup);
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const [students, setStudents] = useState<IStudentOption[]>([]);
  const [groupId, setGroupId] = useState<string | undefined>();
  const [directionId, setDirectionId] = useState<string | undefined>();
  const [groupSearch, setGroupSearch] = useState('');
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('');
  const [formError, setFormError] = useState<string | undefined>();

  const { directions } = useCareerDirections();
  const { enroll, pending } = useCareerMemberMutations();

  const close = useCallback(() => {
    hidePopup(PopupNames.enrollCareerMembersPopup);
  }, [hidePopup]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedGroupSearch(groupSearch.trim()),
      GROUP_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [groupSearch]);

  const { data: groupsData, loading: groupsLoading } = usePaginatedGroupsQuery({
    variables: { query: debouncedGroupSearch || undefined, first: 20 },
    fetchPolicy: 'cache-and-network',
  });

  const groupOptions = useMemo(
    () =>
      (groupsData?.allGroups?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({ value: node.id, label: node.name })),
    [groupsData],
  );

  const directionOptions = useMemo(
    () =>
      directions.map((direction) => ({
        value: direction.id,
        label: direction.title,
      })),
    [directions],
  );

  const loadStudentOptions = useCallback(
    async (input: string): Promise<IStudentOption[]> => {
      try {
        const result = await apollo.query<
          PaginatedStudentsQuery,
          PaginatedStudentsQueryVariables
        >({
          query: PaginatedStudentsDocument,
          variables: { query: input || '', first: 20 },
          fetchPolicy: 'no-cache',
        });
        return (result.data?.allStudents?.edges || [])
          .map((edge) => edge?.node)
          .filter((node): node is NonNullable<typeof node> => Boolean(node))
          .map((node) => {
            const { user } = node;
            const name =
              [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
              user?.email ||
              node.id;
            return {
              value: node.id,
              label: `${name}${user?.email ? ` · ${user.email}` : ''}`,
            };
          });
      } catch {
        return [];
      }
    },
    [apollo],
  );

  const onSubmit = useCallback(async () => {
    setFormError(undefined);
    const studentIds = students.map((option) => option.value);
    // Guard the no-op submit: with neither target the backend enrols nobody
    // and still answers `success`, which reads to the operator as a silent
    // failure.
    if (!studentIds.length && !groupId) {
      setFormError(t('pages.careerCenter.enrollPopup.errorNoTarget'));
      return;
    }
    const result = await enroll({ studentIds, groupId, directionId });
    if (result) {
      data?.onEnrolled?.(result);
      close();
    }
  }, [students, groupId, directionId, enroll, data, close, t]);

  return (
    <PopupContainer
      width="560px"
      title={t('pages.careerCenter.enrollPopup.title')}
      onClose={close}
    >
      <FormBody>
        <Hint>{t('pages.careerCenter.enrollPopup.hint')}</Hint>

        <Field>
          <FieldLabel>
            {t('pages.careerCenter.enrollPopup.students')}
          </FieldLabel>
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            value={students}
            loadOptions={loadStudentOptions}
            onChange={(value) =>
              setStudents(value ? ([...value] as IStudentOption[]) : [])
            }
            placeholder={t(
              'pages.careerCenter.enrollPopup.studentsPlaceholder',
            )}
            noOptionsMessage={() =>
              t('pages.careerCenter.enrollPopup.noStudents')
            }
          />
        </Field>

        <Field>
          <FieldLabel>{t('pages.careerCenter.enrollPopup.group')}</FieldLabel>
          <Select
            showSearch
            allowClear
            style={{ width: '100%' }}
            value={groupId}
            loading={groupsLoading}
            filterOption={false}
            onSearch={setGroupSearch}
            onChange={(value: string | undefined) => setGroupId(value)}
            placeholder={t('pages.careerCenter.enrollPopup.groupPlaceholder')}
            options={groupOptions}
          />
        </Field>

        <Field>
          <FieldLabel>
            {t('pages.careerCenter.enrollPopup.direction')}
          </FieldLabel>
          <Select
            allowClear
            style={{ width: '100%' }}
            value={directionId}
            onChange={(value: string | undefined) => setDirectionId(value)}
            placeholder={t(
              'pages.careerCenter.enrollPopup.directionPlaceholder',
            )}
            options={directionOptions}
          />
        </Field>

        {formError ? <FormError>{formError}</FormError> : null}

        <FormFooter>
          <ActionButton type="button" onClick={close} disabled={pending}>
            {t('pages.careerCenter.enrollPopup.cancel')}
          </ActionButton>
          <ActionButton
            primary
            type="button"
            onClick={onSubmit}
            disabled={pending}
          >
            {pending
              ? t('pages.careerCenter.enrollPopup.submitting')
              : t('pages.careerCenter.enrollPopup.submit')}
          </ActionButton>
        </FormFooter>
      </FormBody>
    </PopupContainer>
  );
});

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
`;

const Hint = styled.div`
  font-size: 13px;
  color: #667085;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #667085;
`;

const FormError = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.Red};
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  border: 1px solid ${({ primary }) => (primary ? '#2e90fa' : '#e4e7ec')};
  background: ${({ primary }) => (primary ? '#2e90fa' : Colors.White)};
  color: ${({ primary }) => (primary ? Colors.White : '#344054')};
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
